import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    CredentialModal,
    MIN_PASSWORD_LENGTH,
    UserFormModal,
    isValidPassword,
    isValidPin,
} from './components';
import { TeamPageSkeleton } from './TeamPageSkeleton';
import { TeamPageView } from './TeamPageView';
import { useTeamPage } from './useTeamPage';

const sanitizePin = (value: string): string =>
    value.replace(/\D/g, '').slice(0, 4);

/**
 * /team page — user management. Owns its data hook (`useTeamPage`) and an
 * `ErrorBoundary`; the throw lives in the inner Content child because React
 * boundaries don't catch errors thrown by the component that renders them.
 * The create/edit, set-PIN, and reset-password modals render outside the
 * boundary so a grid render failure can't take them down.
 */
export const TeamPage: React.FC = () => {
    const page = useTeamPage();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('team.components.userTable.errorTitle'),
            errorMessage: t('team.components.userTable.errorMessage'),
            errorRetry: t('team.components.userTable.errorRetry'),
            pin: {
                title: t('team.components.userTable.pinModal.title'),
                description: t(
                    'team.components.userTable.pinModal.description',
                ),
                label: t('team.components.userTable.pinModal.label'),
                placeholder: t(
                    'team.components.userTable.pinModal.placeholder',
                ),
                submit: t('team.components.userTable.pinModal.submit'),
                cancel: t('team.components.userTable.pinModal.cancel'),
            },
            password: {
                title: t('team.components.userTable.passwordModal.title'),
                description: t(
                    'team.components.userTable.passwordModal.description',
                ),
                label: t('team.components.userTable.passwordModal.label'),
                placeholder: t(
                    'team.components.userTable.passwordModal.placeholder',
                    { min: MIN_PASSWORD_LENGTH },
                ),
                submit: t('team.components.userTable.passwordModal.submit'),
                cancel: t('team.components.userTable.passwordModal.cancel'),
            },
        }),
        [t],
    );

    return (
        <div
            className="mx-auto flex w-full max-w-7xl flex-col gap-8"
            {...getNodeTestIdProp('team-page')}
        >
            <ErrorBoundary
                data-testid="team-page-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="team-page-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <TeamPageContent page={page} />
            </ErrorBoundary>

            <UserFormModal
                branchOptions={page.branchOptions}
                callerBranchId={page.callerBranchId}
                callerRole={page.callerRole}
                error={page.formError}
                initialValues={page.editingInitialValues}
                isOpen={page.isFormOpen}
                isSelf={page.isEditingSelf}
                isSubmitting={page.isFormSubmitting}
                mode={page.formMode}
                onClose={page.onCloseForm}
                onSubmit={page.onSubmitUser}
            />

            <CredentialModal
                cancelLabel={copy.pin.cancel}
                description={copy.pin.description}
                error={page.pinError}
                inputMode="numeric"
                isOpen={page.isPinOpen}
                isSubmitting={page.isPinSubmitting}
                label={copy.pin.label}
                onClose={page.onClosePin}
                onSubmit={page.onSubmitPin}
                placeholder={copy.pin.placeholder}
                sanitize={sanitizePin}
                slot="user-pin"
                submitLabel={copy.pin.submit}
                title={copy.pin.title}
                validate={isValidPin}
            />

            <CredentialModal
                cancelLabel={copy.password.cancel}
                description={copy.password.description}
                error={page.passwordError}
                inputType="password"
                isOpen={page.isPasswordOpen}
                isSubmitting={page.isPasswordSubmitting}
                label={copy.password.label}
                onClose={page.onClosePassword}
                onSubmit={page.onSubmitPassword}
                placeholder={copy.password.placeholder}
                slot="user-password"
                submitLabel={copy.password.submit}
                title={copy.password.title}
                validate={isValidPassword}
            />
        </div>
    );
};

type TeamPageContentProps = {
    page: ReturnType<typeof useTeamPage>;
};

const TeamPageContent: React.FC<TeamPageContentProps> = ({ page }) => {
    if (page.error) {
        throw page.error;
    }

    if (page.isLoading || page.data === null) {
        return <TeamPageSkeleton />;
    }

    return (
        <TeamPageView
            branchRefById={page.branchRefById}
            callerId={page.callerId}
            data={page.data}
            onAddUser={page.onAddUser}
            onDeleteUser={page.onDeleteUser}
            onEditUser={page.onEditUser}
            onOpenPassword={page.onOpenPassword}
            onOpenPin={page.onOpenPin}
            onPageChange={page.onPageChange}
            onPageSizeChange={page.onPageSizeChange}
            onSearchChange={page.onSearchChange}
            page={page.page}
            pageSize={page.pageSize}
            search={page.search}
            showBranchColumn={page.showBranchColumn}
            totalCount={page.totalCount}
            totalPages={page.totalPages}
        />
    );
};
